import { useState, useEffect } from 'react';
import { ChevronDown, Check, Sparkles, Zap, Users, Target, Clock, Shield, Menu, X } from 'lucide-react';
import { MarketingFooter } from '../components/MarketingFooter';
import { SeoHead } from '../components/SeoHead';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "Est-ce vraiment gratuit ?",
    answer: "Oui. 5 mois offerts si vous faites partie des 15 testeurs sélectionnés."
  },
  {
    question: "Dois-je mettre ma carte bancaire ?",
    answer: "Non (optionnel). Le but est de tester, pas de vous piéger."
  },
  {
    question: "Mes données sont-elles en sécurité ?",
    answer: "Oui. Vos données restent confidentielles et ne sont jamais revendues."
  },
  {
    question: "Puis-je tester sans connecter ma banque ?",
    answer: "Oui. Mode démo ou saisie manuelle possible."
  },
  {
    question: "Pourquoi seulement 15 testeurs ?",
    answer: "Pour garantir un support réactif et intégrer les retours rapidement."
  },
  {
    question: "Que se passe-t-il après les 5 mois ?",
    answer: "Vous choisissez : continuer ou arrêter. Sans engagement."
  }
];

export default function BetaFoundersPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Trezoone Beta Founders — Programme complet";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Le programme Beta Founders Trezoone est clos. Trezoone est désormais disponible avec un essai gratuit de 14 jours pour tous.");
    }
  }, []);

  const scrollToInfo = () => {
    const el = document.getElementById('application-form');
    el?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToFeatures = () => {
    const featuresElement = document.getElementById('features');
    featuresElement?.scrollIntoView({ behavior: 'smooth' });
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SeoHead
        title="Beta Founders – Rejoignez les 15 testeurs privilégiés de Trezoone"
        description="Offre exclusive Beta Founders : 5 mois offerts, accès anticipé et influence directe sur le développement de Trezoone. Places limitées à 15 testeurs."
        canonical="/beta-founders"
        keywords="beta founders, testeurs privilégiés, accès anticipé, trezoone beta, version beta gestion financière"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center">
              <img
                src="/logo_trezoone.png"
                alt="TrezoOne"
                className="h-10 w-auto"
                loading="eager"
              />
            </a>

            <nav className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Accueil
              </a>
              <a href="/fonctionnalites" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Fonctionnalités
              </a>
              <a href="/tarifs" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Tarifs
              </a>
            </nav>

            <div className="hidden md:flex items-center space-x-4">
              <a href="/" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Connexion
              </a>
              <a href="/" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                S'inscrire
              </a>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-4 space-y-3">
              <a
                href="/"
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                Accueil
              </a>
              <a
                href="/fonctionnalites"
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                Fonctionnalités
              </a>
              <a
                href="/tarifs"
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
              >
                Tarifs
              </a>
              <div className="pt-3 border-t border-gray-200 space-y-2">
                <a
                  href="/"
                  className="block w-full text-center px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Connexion
                </a>
                <a
                  href="/"
                  className="block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                >
                  S'inscrire
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Programme complet — inscriptions closes
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Trezoone Beta Founders
          </h1>

          <p className="text-2xl md:text-3xl text-blue-600 font-semibold mb-6">
            5 mois offerts pour les 15 premiers testeurs
          </p>

          <p className="text-xl text-slate-600 mb-10 max-w-3xl mx-auto">
            Le programme Beta Founders est maintenant complet. Trezoone est disponible pour tous avec un essai gratuit de 14 jours.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a
              href="/"
              className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
            >
              Essai gratuit 14 jours
            </a>
            <button
              onClick={scrollToFeatures}
              className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              Voir les fonctionnalités
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { icon: Users, text: "15 testeurs" },
              { icon: Check, text: "Accès complet" },
              { icon: Shield, text: "Sans engagement" },
              { icon: Zap, text: "Support prioritaire" }
            ].map((badge, i) => (
              <div key={i} className="bg-white rounded-lg p-4 shadow-sm border border-slate-200">
                <badge.icon className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">{badge.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Pourquoi ce programme ?
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Trezoone est né d'un constat simple : la trésorerie ne se pilote pas avec un solde bancaire et un tableur.
            <br /><br />
            Trezoone vous aide à voir clair, anticiper et sécuriser vos décisions.
          </p>
        </div>
      </section>

      {/* What You Get Section */}
      <section id="features" className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
            Ce que vous obtenez
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg border border-slate-200">
              <div className="text-4xl mb-4">🎁</div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                5 mois d'accès complet offerts
              </h3>
              <ul className="space-y-3">
                {[
                  "Tableau de bord trésorerie",
                  "Budget & prévisionnel",
                  "Scénarios \"Et si… ?\"",
                  "Sécurité Trezoone (stress test zéro encaissement, écart de sécurité, recommandations)"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-8 shadow-lg text-white">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="text-2xl font-bold mb-4">
                Accès prioritaire
              </h3>
              <ul className="space-y-3">
                {[
                  "Support rapide",
                  "Corrections accélérées",
                  "Influence directe sur le produit"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* For Whom Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
            Pour qui ?
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-green-50 rounded-xl p-8 border-2 border-green-200">
              <div className="flex items-center gap-2 mb-4">
                <Check className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-bold text-slate-900">Idéal pour</h3>
              </div>
              <ul className="space-y-2 text-slate-700">
                <li>• Dirigeants TPE/PME</li>
                <li>• Artisans, commerçants, indépendants</li>
                <li>• Associations structurées</li>
              </ul>
            </div>

            <div className="bg-slate-50 rounded-xl p-8 border-2 border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-900">Trezoone n'est pas</h3>
              </div>
              <ul className="space-y-2 text-slate-700">
                <li>• Un logiciel de comptabilité complet</li>
                <li>• Un simple affichage bancaire sans pilotage</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
            Comment ça se passe ?
          </h2>

          <div className="space-y-6">
            {[
              { step: 1, title: "Candidature", desc: "Remplissez le formulaire ci-dessous" },
              { step: 2, title: "Validation sous 24h", desc: "Nous étudions votre candidature" },
              { step: 3, title: "Démarrage", desc: "Données réelles ou mode démo" },
              { step: 4, title: "Retours & amélioration", desc: "Partagez votre expérience" },
              { step: 5, title: "Fin des 5 mois", desc: "Continuer ou arrêter" }
            ].map((item) => (
              <div key={item.step} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
            Questions fréquentes
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-100 transition-colors"
                >
                  <span className="font-semibold text-slate-900">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-600 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && (
                  <div className="px-6 py-4 bg-white border-t border-slate-200">
                    <p className="text-slate-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inscriptions closes */}
      <section id="application-form" className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-slate-800 rounded-xl p-10 border border-slate-700">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Les inscriptions Beta Founders sont closes
            </h2>
            <p className="text-slate-300 text-lg mb-6 leading-relaxed">
              Merci pour votre intérêt ! Les 15 places du programme Beta Founders ont été pourvues.
            </p>
            <p className="text-slate-400 mb-8">
              Trezoone est désormais disponible avec un essai gratuit de 14 jours pour tous. Vous pouvez créer votre compte directement et accéder à toutes les fonctionnalités sans engagement.
            </p>
            <a
              href="/"
              className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
            >
              Démarrer l'essai gratuit 14 jours
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Découvrez Trezoone
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Essai gratuit 14 jours. Accès complet. Sans carte bancaire.
          </p>
          <a
            href="/"
            className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg"
          >
            Commencer gratuitement
          </a>
        </div>
      </section>

        <MarketingFooter />
      </div>
    </>
  );
}
